// @ts-check

import { getBankTransactions } from '@/api/banktransactions'
import { report } from '@/core/sentry'
import {
  paginationGetters,
  paginationMutations,
  paginationState
} from '@/store/paginationHelpers'
import { queryGetters, queryMutations, queryState } from '@/store/queryHelpers'

/** @type {() => BankTransactionModuleState} */
export const initBankTransactionState = () => ({
  bankTransactions: null,
  selectedBankTransactionIds: [],
  ...paginationState,
  ...queryState
})

/**
 * Selects a bank transaction
 * @param {ReturnType<initBankTransactionState>} state
 * @param {BankTransactionResource} transaction
 */
const selectBankTransaction = (state, { id }) => {
  if (state.selectedBankTransactionIds.includes(id)) {
    report('Failed to select a bank transaction', { id })
    return
  }
  state.selectedBankTransactionIds.push(id)
}

/**
 * Unselects a bank transaction
 * @param {BankTransactionModuleState} state
 * @param {BankTransactionResource} transaction
 */
const unselectBankTransaction = (state, { id }) => {
  const index = state.selectedBankTransactionIds.indexOf(id)

  if (index < 0) {
    report('Failed to unselect a bank transaction', { id })
    return
  }

  state.selectedBankTransactionIds.splice(index, 1)
}

/** @type {import('vuex').MutationTree<BankTransactionModuleState>} */
export const bankTransactionMutations = {
  ...paginationMutations,
  ...queryMutations,
  /**
   * Adds new bank transactions
   * @param {BankTransactions} newBankTransactions
   */
  addBankTransactions: (state, newBankTransactions) => {
    /** @type {BankTransactions} */
    let bankTransactions = []

    if (Array.isArray(state.bankTransactions)) {
      bankTransactions = state.bankTransactions
    }

    // Just to be sure no duplicates are displayed in the list and causing
    // unnecessary console errors.
    const oldIds = bankTransactions.map((transaction) => transaction.id)
    const uniqueNewTransactions = newBankTransactions.filter(
      (transaction) => !oldIds.includes(transaction.id)
    )

    state.bankTransactions = bankTransactions.concat(uniqueNewTransactions)
  },
  updateBankTransaction: (state, { id, data }) => {
    const { bankTransactions } = state

    if (!bankTransactions || !bankTransactions.length) {
      report('Failed to update a bank transaction', { id })
      return
    }

    const index = bankTransactions.findIndex(
      (bankTransaction) => bankTransaction.id === id
    )

    if (index < 0) {
      report('Failed to update a bank transaction', { id })
      return
    }

    bankTransactions[index] = { ...bankTransactions[index], ...data }

    state.bankTransactions = [...bankTransactions]
  },
  removeBankTransactions: (state) => {
    state.bankTransactions = null
  },
  selectBankTransaction,
  unselectBankTransaction,
  toggleBankTransaction: (state, transaction) => {
    if (state.selectedBankTransactionIds.includes(transaction.id)) {
      unselectBankTransaction(state, transaction)
    } else {
      selectBankTransaction(state, transaction)
    }
  },
  /**
   * Clears selected bank transactions.
   */
  clearSelectedBankTransactions: (state) => {
    state.selectedBankTransactionIds = []
  }
}

/** @type {import('vuex').GetterTree<BankTransactionModuleState, RootState>} */
export const bankTransactionGetters = {
  ...paginationGetters,
  ...queryGetters,
  bankTransactions: (state) => {
    // is bankTransactions is null
    if (!Array.isArray(state.bankTransactions)) {
      return []
    }

    return state.bankTransactions
  },
  selectedBankTransactions: (state) => {
    return (state.bankTransactions ?? []).filter((transaction) =>
      state.selectedBankTransactionIds.includes(transaction.id)
    )
  },
  bankTransactionType: (state, getters) => {
    return getters.selectedBankTransactions[0]?.type
  },
  hasLoadedBankTransactions: (state) => {
    return Boolean(state.bankTransactions)
  }
}

/** @type {import('vuex').ActionTree<BankTransactionModuleState, RootState>} */
const bankTransactionActions = {
  async getBankTransactions(
    { commit, getters },
    { page = undefined, reset = false } = {}
  ) {
    if (reset === true) {
      commit('removeBankTransactions')
    }

    const { data: bankTransactions, meta } = await getBankTransactions(
      getters.params,
      page
    )
    commit('addBankTransactions', bankTransactions)
    commit('setPaginationData', meta)
    return bankTransactions
  },
  toggleStatusToParams: ({ commit, getters }, status) => {
    if (!getters.params.status) {
      commit('setFilterParams', { status: [status] })
      return
    }

    if (getters.params.status.includes(status)) {
      const newStatuses = getters.params.status.filter(
        (existingStatus) => existingStatus !== status
      )

      commit('setFilterParams', {
        status: newStatuses
      })
    } else {
      commit('setFilterParams', {
        status: [...getters.params.status, status]
      })
      return
    }

    if (getters.params.status.length === 0) {
      commit('removeFilterParam', 'status')
    }
  }
}

export const bankTransactionModule = {
  namespaced: true,
  state: initBankTransactionState,
  actions: bankTransactionActions,
  mutations: bankTransactionMutations,
  getters: bankTransactionGetters
}

export default bankTransactionModule
